/**
 * {@link DataTable} for pairs.
 */
import * as React from 'react';
import { Users } from 'lucide-react';
import { DataTable } from '@/components/common/data-table';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { Button } from '@/components/forms/Button';
import { PairRow } from './PairRow';
import { PairCard } from './PairCard';
import { PairFiltersContent } from './PairFilters';
import { usePairsList, usePair } from '@/hooks/usePairs';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { PairDetails } from './renderPairDetails';
import { useDataTable } from '@/hooks/useDataTable';
import { useCurrentSchoolYearId } from '@/hooks/useCurrentSchoolYearId';
import { cn } from '@/lib/utils';
import {
  DEFAULT_PAIR_FILTERS,
  ALL_PAIR_FILTER_GROUP_IDS,
  PAIR_FILTER_LABELS,
} from '@/components/pairs/pair-configs';
import {
  buildApiFilters,
  hasActiveFilters as checkActiveFilters,
  withPinnedPersonFilter,
  type PairDataTablePageActions,
} from './pair-table-utils';
import type {
  PairFilters,
  PairFilterGroupId,
  PairRecord,
  PairDetailRecord,
  PairQueryParams,
} from '@/types/remote/pair.types';
import type { ViewMode } from '@/types/data-table.types';

/** Props for {@link PairsDataTable}. */
interface Props {
  viewMode?: ViewMode;
  onViewModeChange?: (m: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditPair?: (id: string) => void;
  onDissociatePair?: (id: string) => void;
  onViewSessions?: (pair: PairRecord) => void;
  onAddOneOffSession?: (pair: PairRecord) => void;
  /** Called whenever active API filters change — used by parent for export */
  onFiltersChange?: (filters: PairQueryParams) => void;
  renderDetailsContent?: (pair: PairRecord) => React.ReactNode;
  pageActions?: PairDataTablePageActions;
  onExportExcel?: () => void;
  showCreateButton?: boolean;
  onCreatePair?: () => void;
  isSection?: boolean;
  layoutWidth?: import('@/types/interface-elements.types').ElementWidth;
  /** Deep-link: keep the list on this tutor's pairs (banner lives on the page). */
  pinnedTutorUserId?: string;
  /** Deep-link: keep the list on this student's pairs (banner lives on the page). */
  pinnedStudentUserId?: string;
}

/** Remote pairs list: filters, rows/cards, expand, export. */
export const PairsDataTable: React.FC<Props> = ({
  viewMode: externalViewMode,
  onViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditPair,
  onDissociatePair,
  onViewSessions,
  onAddOneOffSession,
  onFiltersChange,
  renderDetailsContent,
  pageActions,
  onExportExcel,
  showCreateButton,
  onCreatePair,
  isSection = false,
  layoutWidth,
  pinnedTutorUserId,
  pinnedStudentUserId,
}) => {
  /** Current school year — used as the default list filter. */
  const currentSchoolYearId = useCurrentSchoolYearId();
  /** Seed filters with the current school year when it is known. */
  const defaultFilters = React.useMemo(
    () => ({ ...DEFAULT_PAIR_FILTERS, schoolYearId: currentSchoolYearId }),
    [currentSchoolYearId],
  );

  const dt = useDataTable<PairFilters, PairFilterGroupId>({
    tableName: 'pairs',
    defaultFilters,
    allFilterGroupIds: ALL_PAIR_FILTER_GROUP_IDS,
    externalViewMode,
    onExternalViewModeChange: onViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
    isSection,
    layoutWidth,
  });

  const schoolYearInitRef = React.useRef(false);
  /** One-shot: apply current school year once loaded if not already set. */
  React.useEffect(() => {
    if (schoolYearInitRef.current || !currentSchoolYearId) return;
    schoolYearInitRef.current = true;
    dt.primeFilters((prev) => {
      if (prev.schoolYearId) return prev;
      return { ...prev, schoolYearId: currentSchoolYearId };
    });
  }, [currentSchoolYearId]);

  const apiFilters = React.useMemo(
    () =>
      withPinnedPersonFilter(buildApiFilters(dt.filters, dt.currentPage, dt.itemsPerPage), {
        tutorUserId: pinnedTutorUserId,
        studentUserId: pinnedStudentUserId,
      }),
    [dt.filters, dt.currentPage, dt.itemsPerPage, pinnedTutorUserId, pinnedStudentUserId],
  );

  const pinnedPersonKey = `${pinnedTutorUserId ?? ''}:${pinnedStudentUserId ?? ''}`;
  const prevPinnedPersonKeyRef = React.useRef(pinnedPersonKey);
  React.useEffect(() => {
    if (prevPinnedPersonKeyRef.current === pinnedPersonKey) return;
    prevPinnedPersonKeyRef.current = pinnedPersonKey;
    dt.setCurrentPage(1);
  }, [pinnedPersonKey, dt.setCurrentPage]);

  const { data: apiResponse, isLoading } = usePairsList(apiFilters);

  /** Mirror active filters to the parent so export uses the same query. */
  React.useEffect(() => {
    onFiltersChange?.(apiFilters);
  }, [apiFilters, onFiltersChange]);

  const data = apiResponse?.data ?? [];
  const totalItems = apiResponse?.total ?? 0;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = Math.min(startIndex + dt.itemsPerPage, totalItems);

  const {
    data: expandedPairData,
    isLoading: isLoadingExpandedPair,
    isError: isExpandedPairError,
    error: expandedPairError,
  } = usePair(expandedRowId);
  const expandedItem = (expandedPairData ?? null) as PairRecord | null;
  useUnitGetNotFound({
    isError: isExpandedPairError,
    error: expandedPairError,
    catalogKey: 'pairNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => checkActiveFilters(dt.filters), [dt.filters]);

  const customFilterRender = React.useCallback(
    () => (
      <PairFiltersContent
        filters={dt.filters}
        visibleFilterIds={dt.visibleFilterIds as PairFilterGroupId[]}
        onFilterChange={(patch) => {
          dt.setFilters((prev) => ({ ...prev, ...patch }));
          dt.setCurrentPage(1);
        }}
      />
    ),
    [dt.filters, dt.visibleFilterIds, dt.setFilters, dt.setCurrentPage],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: customFilterRender,
      },
    ],
    [customFilterRender],
  );

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="binômes"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          <Button
            preset="toolbar-filter"
            className={cn(dt.showFilters && 'section-action-button-active')}
            showBadge={hasActiveFilters}
            onClick={() => {
              if (!dt.showFilters) dt.setShowConfigure(false);
              dt.setShowFilters((prev) => !prev);
            }}
          />
          {pageActions?.canExportExcel && onExportExcel && (
            <Button
              preset="toolbar-export"
              tooltipText="Exporter les binômes en Excel"
              onClick={onExportExcel}
            />
          )}
          {showCreateButton && onCreatePair && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un binôme"
              onClick={() => {
                onCloseDetails?.();
                onCreatePair();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        <DataTableFilters
          show={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={() => {
            dt.clearFilters();
          }}
          resultCount={totalItems}
          resultLabel="binôme(s)"
          onToggleConfigure={
            dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
          }
          showConfigure={dt.showConfigure}
          configureContent={
            <div className="flex flex-wrap gap-3">
              {ALL_PAIR_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map(
                (id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn ? 'filter-option-primary--active' : 'filter-option-primary',
                      )}
                    >
                      {PAIR_FILTER_LABELS[id]}
                    </Button>
                  );
                },
              )}
            </div>
          }
        />
      }
      renderTableHead={() => (
        <tr>
          <th>Tuteur</th>
          <th>Élève</th>
          <th>Statut</th>
          <th>Séances</th>
          <th>Comptes rendus</th>
          <th className="w-36 text-center">Actions</th>
        </tr>
      )}
      rowClassName={(pair: PairRecord) =>
        cn('pair-row', dt.expandedRowId === pair.id && 'ring-1 ring-inset')
      }
      renderTableRow={(pair: PairRecord) => (
        <PairRow
          pair={pair}
          onView={() => onToggleRow?.(pair.id)}
          onEdit={pageActions?.canEditRow ? () => onEditPair?.(pair.id) : undefined}
          onDissociate={pageActions?.canDissociate ? () => onDissociatePair?.(pair.id) : undefined}
          onViewSessions={
            pageActions?.canViewSessions !== false && onViewSessions
              ? () => onViewSessions(pair)
              : undefined
          }
          onAddOneOffSession={
            pageActions?.canAddOneOffSession && onAddOneOffSession
              ? () => onAddOneOffSession(pair)
              : undefined
          }
        />
      )}
      renderCard={(pair: PairRecord) => (
        <PairCard
          pair={pair}
          onView={() => onToggleRow?.(pair.id)}
          onEdit={pageActions?.canEditRow ? () => onEditPair?.(pair.id) : undefined}
          onDissociate={pageActions?.canDissociate ? () => onDissociatePair?.(pair.id) : undefined}
          onViewSessions={
            pageActions?.canViewSessions !== false && onViewSessions
              ? () => onViewSessions(pair)
              : undefined
          }
          onAddOneOffSession={
            pageActions?.canAddOneOffSession && onAddOneOffSession
              ? () => onAddOneOffSession(pair)
              : undefined
          }
        />
      )}
      expandedRowId={dt.expandedRowId}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      expandedItem={expandedItem}
      isExpandedDetailLoading={isLoadingExpandedPair}
      renderExpandedRow={(pair) =>
        renderDetailsContent?.(pair) ?? (
          <PairDetails
            pair={pair as PairDetailRecord}
            onEdit={onEditPair}
            onDissociate={onDissociatePair}
            onViewSessions={onViewSessions ? () => onViewSessions(pair) : undefined}
            onAddOneOffSession={
              pageActions?.canAddOneOffSession && onAddOneOffSession
                ? () => onAddOneOffSession(pair)
                : undefined
            }
          />
        )
      }
      renderDetailsContent={(pair) =>
        renderDetailsContent?.(pair) ?? (
          <PairDetails
            pair={pair as PairDetailRecord}
            onEdit={onEditPair}
            onDissociate={onDissociatePair}
            onViewSessions={onViewSessions ? () => onViewSessions(pair) : undefined}
            onAddOneOffSession={
              pageActions?.canAddOneOffSession && onAddOneOffSession
                ? () => onAddOneOffSession(pair)
                : undefined
            }
          />
        )
      }
      onCloseDetails={onCloseDetails}
      emptyIcon={Users}
      emptyText="Aucun binôme trouvé"
      isSection={isSection}
      title="Binômes"
      sidebarMenuId="menu-pairs"
    />
  );
};
